import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'

import { Country, CountrySearchParams, Province } from './country.model'
import { Page } from '../../models/util.model'
import { difference, uniq } from 'lodash'
import { COUNTRIES_LIST, COUNTRY_EU_CODES } from '../../consts/country.const'

@Injectable({
  providedIn: 'root',
})
export class CountriesService {
  /**
   * Read a country by ID
   * @param countryId - The country ID
   * @returns The observable<Country> for read the country
   */
  read$(countryId: string): Observable<Country> {
    // For _id, we'll use the index as ID (not ideal but matches the pattern)
    const index = parseInt(countryId)
    const country = COUNTRIES_LIST[index]
    return of(country)
  }

  /**
   * Read a country by code
   * @param alpha2Code - The country code
   * @returns The Country for read the country
   */
  readByCode(alpha2Code: string): Country | undefined {
    return COUNTRIES_LIST.find((c) => c.alpha2Code === alpha2Code)
  }

  /**
   * Find a country by params
   * @param params - The search params
   * @returns the observable<Country> for find a country
   */
  readOne$(params?: CountrySearchParams): Observable<Country> {
    if (!params) {
      return of(COUNTRIES_LIST[0]) // Return first country as default
    }

    const sortedCountries = this.sortCountries(COUNTRIES_LIST)
    const filteredCountries = this.applyFilters(sortedCountries, params)
    return of(filteredCountries[0])
  }

  /**
   * Search countries by params
   * @param params - The search params
   * @returns The observable<Page<Country>> for search countries
   */
  search$(params?: CountrySearchParams): Observable<Page<Country>> {
    const sortedCountries = this.sortCountries(COUNTRIES_LIST)
    const filteredCountries = this.applyFilters(sortedCountries, params)

    const page: Page<Country> = {
      data: filteredCountries,
      totalCount: COUNTRIES_LIST.length,
    }

    return of(page)
  }

  /**
   * Search provinces by params
   * @param params - The search params
   * @returns The observable<Page<Province>> for search provinces
   */
  searchProvince$(
    countryCode: string,
    params?: { code?: string; name?: string },
  ): Observable<Page<Province>> {
    const country = COUNTRIES_LIST.find((c) => c.alpha2Code === countryCode)
    if (!country) {
      return of({ data: [], totalCount: 0 })
    }

    const code = params?.code ?? ''
    const name = params?.name ?? ''

    if (!params || (!code && !name)) {
      return of({
        data: country.provinces ?? [],
        totalCount: (country.provinces ?? []).length,
      })
    }

    const provincesByCode = code
      ? (country.provinces ?? []).filter((p) =>
          p.code.toLowerCase().includes(code.toLowerCase()),
        )
      : undefined
    const provincesByName = name
      ? (country.provinces ?? []).filter((p) =>
          p.name.toLowerCase().includes(name.toLowerCase()),
        )
      : undefined

    const provinces = provincesByCode?.length
      ? provincesByCode
      : provincesByName?.length
        ? provincesByName
        : []

    const page: Page<Province> = {
      data: provinces,
      totalCount: provinces.length,
    }
    return of(page)
  }

  /**
   * List countries by params
   * @param params - The search params
   * @returns The observable<Country[]> for list countries
   */
  list$(params?: CountrySearchParams): Observable<Country[]> {
    const sortedCountries = this.sortCountries(COUNTRIES_LIST)
    const filteredCountries = this.applyFilters(sortedCountries, params)
    return of(filteredCountries)
  }

  /**
   * List countries by params
   * @param params - The search params
   * @returns The observable<Country[]> for list countries
   */
  listProvinces$(params?: {
    countryCode: string
    code?: string
    name?: string
  }): Observable<Province[]> {
    const country = COUNTRIES_LIST.find(
      (c) => c.alpha2Code === params?.countryCode,
    )
    if (!country?.provinces?.length) {
      return of([])
    }

    const code = params?.code ?? ''
    const name = params?.name ?? ''

    if (!params || (!code && !name)) {
      return of(country.provinces ?? [])
    }

    const provincesByCode = code
      ? (country.provinces ?? []).filter((p) =>
          p.code.toLowerCase().includes(code.toLowerCase()),
        )
      : undefined
    const provincesByName = name
      ? (country.provinces ?? []).filter((p) =>
          p.name.toLowerCase().includes(name.toLowerCase()),
        )
      : undefined

    const provinces = provincesByCode?.length
      ? provincesByCode
      : provincesByName?.length
        ? provincesByName
        : []

    return of(provinces)
  }

  /**
   * List countries by params
   * @param params - The search params
   * @returns The observable<Country[]> for list countries
   */
  cleanList(params?: CountrySearchParams): Country[] {
    const sortedCountries = this.sortCountries(COUNTRIES_LIST)
    const filteredCountries = this.applyFilters(sortedCountries, params)
    return filteredCountries
  }

  /**
   * List countries by params
   * @param params - The search params
   * @returns The observable<Country[]> for list countries
   */
  cleanUEList(params?: CountrySearchParams): Country[] {
    const sortedCountries = this.sortCountries(COUNTRIES_LIST)
    const filteredCountries = this.applyFilters(sortedCountries, params)
    const ueCountries = filteredCountries.filter((c) =>
      COUNTRY_EU_CODES.includes(c.alpha2Code),
    )
    return ueCountries
  }

  /**
   * Generate countries store
   * @param countryKeys - the country keys to load
   * @param countries - the countries already loaded
   * @returns the Observable<Country[]> as store
   */
  store$(
    countryKeys: string[],
    countries: Country[],
    key: 'alpha2Code' = 'alpha2Code',
  ): Observable<Country[]> {
    countryKeys = uniq(countryKeys)
    countryKeys = difference(
      countryKeys,
      countries.map((u) => u[key]),
    )

    if (countryKeys.length === 0) {
      return of(countries)
    }

    // Filter countries directly from COUNTRIES_LIST
    const newCountries = COUNTRIES_LIST.filter((country) =>
      countryKeys.includes(country[key]),
    )

    return of([...countries, ...newCountries])
  }

  /**
   * Generate provincecs store
   * @param countryKeys - the province keys to load
   * @param countries - the provincecs already loaded
   * @returns the Observable<Province[]> as store
   */
  storeProvince$(
    countryCode?: string,
    provinceKeys?: string[],
    key: 'code' = 'code',
  ): Observable<Province[]> {
    if (!countryCode) {
      return of([])
    }
    const country = COUNTRIES_LIST.find((c) => c.alpha2Code === countryCode)
    if (!country) {
      return of([])
    }
    const provinces = country.provinces ?? []

    provinceKeys = uniq(provinceKeys)
    provinceKeys = difference(
      provinceKeys,
      provinces.map((u) => u[key]),
    )

    if (provinceKeys.length === 0) {
      return of(provinces)
    }

    const newProvinces = provinces.filter((province) =>
      provinceKeys.includes(province[key]),
    )

    return of([...provinces, ...newProvinces])
  }

  /**
   * Apply filters to countries list
   * @param countries - The countries to filter
   * @param params - The search params
   * @returns The filtered countries
   */
  private applyFilters(
    countries: Country[],
    params?: CountrySearchParams,
  ): Country[] {
    let filteredCountries = [...countries]

    if (!params) {
      return filteredCountries
    }

    if (params['name:ct']) {
      const searchTerm = params['name:ct'].toLowerCase()
      filteredCountries = filteredCountries.filter((country) =>
        country.name.toLowerCase().includes(searchTerm),
      )
    }

    if (params.name) {
      const names = Array.isArray(params.name) ? params.name : [params.name]
      filteredCountries = filteredCountries.filter((country) =>
        names.some((name) =>
          country.name.toLowerCase().includes(name.toLowerCase()),
        ),
      )
    }

    if (params.alpha2Code) {
      if (!Array.isArray(params.alpha2Code)) {
        filteredCountries = filteredCountries.filter(
          (country) =>
            `${country.alpha2Code}`.toLowerCase() ===
            `${params.alpha2Code ?? ''}`.toLowerCase(),
        )
      } else {
        filteredCountries = filteredCountries.filter((country) =>
          ((params.alpha2Code ?? []) as string[])
            .map((c) => c.toLowerCase())
            .includes(`${country.alpha2Code}`.toLowerCase()),
        )
      }
    }

    // Handle _id filtering (for specific country keys)
    if (params._id) {
      const countryKeys = Array.isArray(params._id) ? params._id : [params._id]
      filteredCountries = filteredCountries.filter((country) => {
        const index = COUNTRIES_LIST.indexOf(country)
        return countryKeys.includes(index.toString())
      })
    }

    return filteredCountries
  }

  /**
   * Sort countries with Italy first, then alphabetically by name
   * @param countries - The countries to sort
   * @returns The sorted countries
   */
  private sortCountries(countries: Country[]): Country[] {
    const sortedCountries = [...countries]

    // Custom sorting: Italy first, then alphabetically by name
    sortedCountries.sort((a, b) => {
      if (a.alpha3Code === 'ITA') return -1
      if (b.alpha3Code === 'ITA') return 1
      return a.name.localeCompare(b.name)
    })

    // Sort provinces alphabetically by name for each country
    sortedCountries.forEach((country) => {
      if (country.provinces) {
        country.provinces.sort((a, b) => a.name.localeCompare(b.name))
      }
    })

    return sortedCountries
  }
}
